import type { ConsoleSkill, ConsoleSkillFile, TenantSettings } from '@plato/shared';
import { type ChangeEvent, useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';

const loadSkills = (data: ConsoleData, tenant: TenantSummary) => data.listSkills(tenant.id);
const loadSettings = (data: ConsoleData, tenant: TenantSummary) =>
  data.getAssistantSettings(tenant.id);

const STATUS_LABELS: Record<ConsoleSkill['status'], string> = {
  draft: 'Draft',
  listed: 'In the marketplace',
  withdrawn: 'Withdrawn',
};

export function SkillsPage() {
  const { query, reload } = useReloadableTenantQuery(loadSkills);
  const canEdit = useHasRole('editor');
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      {!canEdit && <ReadOnlyNote needs="editor" />}
      <PersonalSkillsSetting canEdit={canEdit} />
      <section>
        <h2>Marketplace</h2>
        <p className="muted">
          Skills your users can add to the assistant. A skill is a folder with a SKILL.md: a name, a
          description that says when to use it, and instructions, plus any scripts or reference
          files they mention. Upload it as a draft, try it, then put it in the marketplace. An
          always-on skill is in every user's assistant unless they switch it off.
        </p>
        {canEdit && <UploadSkill label="Add a skill" onUploaded={reload} />}
        <Loaded query={query}>
          {(skills) =>
            skills.length === 0 ? (
              <p className="muted">No skills yet.</p>
            ) : (
              <SkillTable skills={skills} canEdit={canEdit} onOpen={setOpen} onChanged={reload} />
            )
          }
        </Loaded>
      </section>
      {open !== null && (
        <SkillDetail
          skillId={open}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
          onChanged={reload}
        />
      )}
    </>
  );
}

function PersonalSkillsSetting(props: { canEdit: boolean }) {
  const { data, tenant } = useConsole();
  const { query, reload } = useReloadableTenantQuery(loadSettings);
  const action = useAction();
  const change = (on: boolean) =>
    void action
      .run(async () => {
        await data.updateAssistantSettings(tenant.id, { personalSkills: on });
      })
      .then((ok) => ok && reload());
  return (
    <section>
      <h2>Users' own skills</h2>
      <Loaded query={query}>
        {(settings: TenantSettings) => (
          <div className="row">
            <p>
              Users can ask the assistant to save a way of working as a skill of their own, which
              only they see.{' '}
              <span className={settings.personalSkills ? 'chip chip-success' : 'chip'}>
                {settings.personalSkills ? 'On' : 'Off'}
              </span>
            </p>
            {props.canEdit && (
              <button
                type="button"
                disabled={action.pending}
                onClick={() => change(!settings.personalSkills)}
              >
                {settings.personalSkills ? 'Switch off' : 'Switch on'}
              </button>
            )}
          </div>
        )}
      </Loaded>
      <ActionError error={action.error} />
    </section>
  );
}

function SkillTable(props: {
  skills: readonly ConsoleSkill[];
  canEdit: boolean;
  onOpen: (skillId: string) => void;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const update = (skill: ConsoleSkill, change: Parameters<ConsoleData['updateSkill']>[2]) =>
    void action
      .run(async () => {
        await data.updateSkill(tenant.id, skill.id, change);
      })
      .then((ok) => ok && props.onChanged());
  return (
    <>
      <DataTable
        caption="Skills in the marketplace"
        columns={[
          {
            header: 'Skill',
            cell: (row) => (
              <>
                <button type="button" className="link" onClick={() => props.onOpen(row.id)}>
                  <code>{row.name}</code>
                </button>
                <div className="muted">{row.description}</div>
              </>
            ),
          },
          {
            header: 'Status',
            cell: (row) => (
              <>
                <span className={row.status === 'listed' ? 'chip chip-success' : 'chip'}>
                  {STATUS_LABELS[row.status]}
                </span>
                {row.always_on && <span className="chip"> Always on</span>}
              </>
            ),
          },
          { header: 'Uses', cell: (row) => row.invocation_count.toLocaleString() },
          { header: 'Version', cell: (row) => row.version },
          { header: 'Updated', cell: (row) => formatDate(row.updated_at) },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: ConsoleSkill) => (
                    <div className="row">
                      {row.status === 'listed' ? (
                        <button
                          type="button"
                          disabled={action.pending}
                          aria-label={`Withdraw ${row.name}`}
                          onClick={() => update(row, { status: 'withdrawn' })}
                        >
                          Withdraw
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={action.pending}
                          aria-label={`Put ${row.name} in the marketplace`}
                          onClick={() => update(row, { status: 'listed' })}
                        >
                          Put in marketplace
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={action.pending}
                        aria-label={`${row.always_on ? 'Stop making' : 'Make'} ${row.name} always on`}
                        onClick={() => update(row, { always_on: !row.always_on })}
                      >
                        {row.always_on ? 'Not always on' : 'Always on'}
                      </button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.skills}
      />
      <ActionError error={action.error} />
    </>
  );
}

function SkillDetail(props: {
  skillId: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const load = (data: ConsoleData, tenant: TenantSummary) =>
    data.getSkill(tenant.id, props.skillId);
  const { query, reload } = useReloadableTenantQuery(load);
  const { data, tenant } = useConsole();
  const action = useAction();
  const remove = () =>
    void action
      .run(() => data.deleteSkill(tenant.id, props.skillId))
      .then((ok) => {
        if (ok) {
          props.onClose();
          props.onChanged();
        }
      });
  return (
    <section aria-label="Skill">
      <Loaded query={query}>
        {(skill) => (
          <>
            <div className="row">
              <h2>
                <code>{skill.name}</code> <span className="chip">Version {skill.version}</span>
              </h2>
              <button type="button" className="quiet" onClick={props.onClose}>
                Close
              </button>
            </div>
            <p>{skill.description}</p>
            <h3>Instructions</h3>
            <pre className="skill-instructions">{skill.instructions}</pre>
            <h3>Files</h3>
            <ul>
              {skill.files.map((file) => (
                <li key={file.path}>
                  <code>{file.path}</code>
                </li>
              ))}
            </ul>
            <h3>Versions</h3>
            <ul>
              {skill.versions.map((version) => (
                <li key={version.version}>
                  Version {version.version}, {formatDate(version.created_at)}
                </li>
              ))}
            </ul>
            {props.canEdit && (
              <div className="row">
                <UploadSkill
                  label="Upload a new version"
                  skillId={skill.id}
                  onUploaded={() => {
                    reload();
                    props.onChanged();
                  }}
                />
                <button type="button" className="danger" disabled={action.pending} onClick={remove}>
                  Delete skill
                </button>
              </div>
            )}
            <ActionError error={action.error} />
          </>
        )}
      </Loaded>
    </section>
  );
}

function UploadSkill(props: { label: string; skillId?: string; onUploaded: () => void }) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const upload = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    event.target.value = '';
    if (chosen.length === 0) return;
    void action
      .run(async () => {
        const files = await skillFiles(chosen);
        if (props.skillId === undefined) await data.addSkill(tenant.id, files);
        else await data.addSkillVersion(tenant.id, props.skillId, files);
      })
      .then((ok) => ok && props.onUploaded());
  };
  return (
    <div className="row">
      <label className="button">
        {props.label}: choose its folder
        <input
          type="file"
          className="visually-hidden"
          multiple
          disabled={action.pending}
          onChange={upload}
          {...{ webkitdirectory: '' }}
        />
      </label>
      <label className="button quiet">
        or just a SKILL.md
        <input
          type="file"
          className="visually-hidden"
          accept=".md,text/markdown"
          disabled={action.pending}
          onChange={upload}
        />
      </label>
      <ActionError error={action.error} />
    </div>
  );
}

/** The files of a chosen folder by their path inside it, leaving out hidden and system files. */
export async function skillFiles(chosen: readonly File[]): Promise<ConsoleSkillFile[]> {
  const files: ConsoleSkillFile[] = [];
  for (const file of chosen) {
    const relative = file.webkitRelativePath || file.name;
    const segments = relative.split('/');
    const path = (segments.length > 1 ? segments.slice(1) : segments).join('/');
    if (path.split('/').some((segment) => segment.startsWith('.') || segment === '__MACOSX')) {
      continue;
    }
    files.push({ path: segments.length === 1 ? 'SKILL.md' : path, content: await file.text() });
  }
  return files;
}
